import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, CurrencyField, FormGrid, Modal, NumberField, Pill, TextField, TextareaField, fmtBRL } from "@atlas/ui/web";
import type { ContratoPortavel, Portabilidade, PortabilidadeDetalhe } from "@atlas/sdk";
import { atlas } from "../lib/sdk";
import { mensagemDeErro } from "../lib/erro-validacao";
import {
  ESTADO_PORTABILIDADE_OPERADOR,
  percentualParaFracao,
  pillDaPortabilidade,
  prazoDaEtapa,
} from "../lib/portabilidade";
import { downloadAuthed } from "../routes/prefeitura/_ui";
import { fmtDataHora } from "../lib/refinanciamento";

function Dado({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, fontWeight: 600 }}>{v}</div>
    </div>
  );
}

/** Origem, saldo e prazo — o cabecalho de toda etapa. */
export function ResumoPortabilidade({ p }: { p: Portabilidade }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
        gap: 10,
        padding: 12,
        borderRadius: 10,
        background: "var(--bg-elev-2)",
        fontSize: 13,
        marginBottom: 14,
      }}
    >
      {p.fluxoLegado ? <p style={{ gridColumn: "1 / -1", margin: 0, color: "var(--text-muted)" }}>Operação iniciada antes da atualização. A etapa e os registros anteriores foram preservados.</p> : null}
      {p.analiseManual ? <p style={{ gridColumn: "1 / -1", margin: 0 }}>Credora sem cadastro ativo. Portabilidade aprovada automaticamente; o comprovante de pagamento será analisado pela averbadora. Saldo de referência: {fmtBRL(p.origem.saldoDevedor)}.</p> : null}
      {p.aceiteCredoraEm ? <Dado label="Aceite da credora" v={fmtDataHora(p.aceiteCredoraEm)} /> : null}
      <Dado label="Servidor" v={`${p.servidorNome} · ${p.matricula}`} />
      <Dado label="Contrato de origem" v={`${p.origem.adf} (${p.credora.nome})`} />
      <Dado label="Parcela atual" v={fmtBRL(p.origem.parcela)} />
      <Dado label="Saldo informado" v={p.saldoInformado == null ? "—" : fmtBRL(p.saldoInformado)} />
      <Dado label="Proponente" v={p.proponente.nome} />
      <Dado label="Prazo da etapa" v={prazoDaEtapa(p.expiraEm) ?? "—"} />
    </div>
  );
}

/** O proponente procura o servidor e abre a portabilidade de um contrato de outro credor. */
export function AbrirPortabilidadeModal({ onClose, onFeito }: { onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const [cpf, setCpf] = useState("");
  const [matricula, setMatricula] = useState("");
  const [busca, setBusca] = useState<{ cpf?: string; matricula?: string } | null>(null);
  const [adf, setAdf] = useState<string | null>(null);
  const [propor, setPropor] = useState(false);
  const q = useQuery({
    queryKey: ["banco", "portabilidade", "elegiveis", busca],
    queryFn: () => atlas.portabilidade.elegiveisBanco(busca!),
    enabled: busca != null,
    retry: false,
  });
  const escolhido = q.data?.itens.flatMap((i) => i.contratos).find((c) => c.adf === adf);
  const itens = q.data?.itens ?? [];
  const podeBuscar = cpf.replace(/\D/g, "").length === 11 || matricula.trim().length > 0;
  if (propor && escolhido) return <InformarCondicoesModal origem={escolhido} onClose={onClose} onFeito={onFeito} />;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      eyebrow="Portabilidade"
      title="Abrir portabilidade"
      subtitle="Traga para a sua consignatária um contrato que o servidor tem em outro credor. Ele confirma no app antes de a credora ser acionada."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={() => setPropor(true)} disabled={!adf}>
            Continuar: condições da proposta
          </Button>
        </>
      }
    >
      <FormGrid>
        <TextField label="CPF do servidor" value={cpf} onChange={(e) => setCpf(e.target.value)} inputMode="numeric" placeholder="000.000.000-00" />
        <TextField label="ou matrícula" value={matricula} onChange={(e) => setMatricula(e.target.value)} />
      </FormGrid>
      <div style={{ margin: "10px 0 14px" }}>
        <Button
          size="sm"
          variant="ghost"
          disabled={!podeBuscar || q.isFetching}
          onClick={() => {
            setAdf(null);
            setBusca(cpf.replace(/\D/g, "").length === 11 ? { cpf: cpf.replace(/\D/g, "") } : { matricula: matricula.trim() });
          }}
        >
          {q.isFetching ? "Buscando…" : "Buscar contratos"}
        </Button>
      </div>
      {q.error ? <p style={{ color: "var(--danger-500)" }}>{mensagemDeErro(q.error, "Servidor não encontrado nas folhas em que a consignatária tem convênio.")}</p> : null}
      {itens.map((s) => (
        <div key={`${s.unidadeId}-${s.matricula}`} style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {s.servidorNome} <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>· {s.cpfMasked} · matrícula {s.matricula}</span>
          </div>
          {s.contratos.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "6px 0 0" }}>Nenhum empréstimo em outro credor nesta matrícula.</p>
          ) : (
            <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
              {s.contratos.map((c) => (
                <label key={c.adf} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, opacity: c.elegivel ? 1 : 0.6 }}>
                  <input type="radio" name="origem" checked={adf === c.adf} disabled={!c.elegivel} onChange={() => setAdf(c.adf)} />
                  <span style={{ fontFamily: "var(--font-mono)" }}>{c.adf}</span>
                  <span>
                    {c.credora.nome} · parcela {fmtBRL(c.valorParcela)} · saldo {fmtBRL(c.saldoDevedor)} · {c.parcelasPagas}/{c.totalParcelas} pagas
                  </span>
                  {c.motivo ? <span style={{ color: "var(--gold-500)" }}>— {c.motivo}</span> : null}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}

    </Modal>
  );
}

/** Proposta apresentada antes do consentimento e do pagamento. */
export function InformarCondicoesModal({ p, origem, onClose, onFeito }: { p?: Portabilidade; origem?: ContratoPortavel; onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const saldo = p?.saldoInformado ?? p?.origem.saldoDevedor ?? origem?.saldoDevedor ?? 0;
  const parcelaOrigem = p?.origem.parcela ?? origem?.valorParcela ?? 0;
  const c = p?.condicoes;
  const [valor, setValor] = useState<number | null>(c?.valorFinanciado ?? (saldo > 0 ? saldo : null));
  const [liquido, setLiquido] = useState<number | null>(c?.valorLiquido ?? null);
  const [parcela, setParcela] = useState<number | null>(c?.valorParcela ?? null);
  const [prazo, setPrazo] = useState(c?.prazo ? String(c.prazo) : "");
  const [cet, setCet] = useState(c ? String(c.cetAm * 100).replace(".", ",") : "");
  const [juros, setJuros] = useState(c?.taxaAm ? String(c.taxaAm * 100).replace(".", ",") : "");
  const [numero, setNumero] = useState(c?.numeroContratoBanco ?? "");
  const cetAm = percentualParaFracao(cet);
  const taxaAm = juros.trim() ? percentualParaFracao(juros) : undefined;
  const prazoNum = Number(prazo);
  const naoCobre = valor != null && valor + 0.005 < saldo;
  const diferenca = useMemo(() => (parcela == null ? null : Math.round((parcela - parcelaOrigem) * 100) / 100), [parcela, parcelaOrigem]);
  const pronto = valor != null && !naoCobre && parcela != null && parcela > 0 && Number.isInteger(prazoNum) && prazoNum >= 1 && prazoNum <= 120 && cetAm != null && taxaAm !== null && numero.trim() !== "";
  const mut = useMutation({
    mutationFn: () => {
      const condicoes = {
        valorFinanciado: valor!,
        valorLiquido: liquido ?? undefined,
        valorParcela: parcela!,
        prazo: prazoNum,
        cetAm: cetAm!,
        taxaAm: taxaAm ?? undefined,
        numeroContratoBanco: numero.trim(),
      };
      return p ? atlas.portabilidade.informarCondicoes(p.id, condicoes) : atlas.portabilidade.abrir(origem!.adf, condicoes);
    },
    onSuccess: onFeito,
  });
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      eyebrow={p ? `Portabilidade ${p.id}` : "Nova portabilidade"}
      title={p?.fluxoLegado ? "Completar dados do contrato" : "Condições da proposta"}
      subtitle={p?.fluxoLegado ? "Complete os dados do contrato de destino para continuar esta operação anterior. Isso não refaz o aceite nem informa um pagamento." : "Informe as condições que o servidor deverá conferir e aceitar antes de a credora ser acionada."}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={() => mut.mutate()} disabled={!pronto || mut.isPending}>
            {mut.isPending ? "Enviando…" : p?.fluxoLegado ? "Salvar dados do contrato" : "Enviar proposta ao servidor"}
          </Button>
        </>
      }
    >
      {p ? <ResumoPortabilidade p={p} /> : <p>Contrato {origem?.adf} · {origem?.credora.nome} · saldo registrado {fmtBRL(saldo)}</p>}
      <FormGrid>
        <CurrencyField
          label="Valor financiado"
          value={valor}
          onValueChange={setValor}
          required
          error={naoCobre ? `Precisa cobrir o saldo informado (${fmtBRL(saldo)}).` : undefined}
        />
        <CurrencyField
          label="Valor líquido ao servidor"
          value={liquido}
          onValueChange={setLiquido}
          hint={valor != null ? `Vazio = ${fmtBRL(Math.max(0, valor - saldo))} (financiado − saldo).` : "Vazio = financiado − saldo."}
        />
        <CurrencyField label="Parcela" value={parcela} onValueChange={setParcela} required hint="O valor que entra na folha. Calcule no seu sistema." />
        <NumberField label="Prazo (parcelas)" value={prazo} onChange={(e) => setPrazo(e.target.value)} min={1} max={120} required />
        <TextField label="CET (% a.m.)" value={cet} onChange={(e) => setCet(e.target.value)} inputMode="decimal" placeholder="1,85" required hint="Passa pelo teto de CET do convênio." />
        <TextField
          label="Juros (% a.m.)"
          value={juros}
          onChange={(e) => setJuros(e.target.value)}
          inputMode="decimal"
          placeholder="1,65"
          error={taxaAm === null ? "Percentual inválido." : undefined}
        />
        <TextField label="Número do contrato no seu sistema" value={numero} onChange={(e) => setNumero(e.target.value)} required />
      </FormGrid>
      {diferenca != null ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Diferença para a parcela atual: <b>{diferenca > 0 ? "+" : diferenca < 0 ? "−" : ""} {fmtBRL(Math.abs(diferenca))}</b>. A margem é
          conferida contando a parcela da origem, que sai da folha na liquidação.
        </p>
      ) : null}
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível apresentar a proposta.")}</p> : null}
    </Modal>
  );
}

/** Registro do valor transferido à credora, sem cadastrar ou alterar o contrato. */
export function InformarPagamentoModal({ p, onClose, onFeito }: { p: Portabilidade; onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const [valorPago, setValorPago] = useState<number | null>(p.saldoInformado);
  const [arquivo, setArquivo] = useState<File | undefined>();
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);
  const saldo = p.saldoInformado ?? 0;
  const cobre = valorPago != null && valorPago > 0 && Math.round(valorPago * 100) >= Math.round(saldo * 100);
  const mut = useMutation({ mutationFn: () => atlas.portabilidade.informarPagamento(p.id, { valorPago: valorPago!, comprovante: arquivo }), onSuccess: onFeito });
  return <Modal open onClose={onClose} eyebrow={`Portabilidade ${p.id}`} title="Informar pagamento"
    subtitle={`Informe o valor pago a ${p.credora.nome} para quitar o contrato de origem.`}
    footer={<><Button variant="ghost" onClick={onClose}>Fechar</Button><Button onClick={() => mut.mutate()} disabled={!cobre || !!erroArquivo || (p.analiseManual && !arquivo) || mut.isPending}>{mut.isPending ? "Enviando…" : "Informar pagamento"}</Button></>}>
    <ResumoPortabilidade p={p} />
    <CurrencyField label="Valor pago à credora" value={valorPago} onValueChange={setValorPago} required error={valorPago != null && !cobre ? `O pagamento deve cobrir ${fmtBRL(saldo)}.` : undefined} />
    {p.motivo ? <p>Correção solicitada: {p.motivo}</p> : null}
    <label style={{ display: "grid", gap: 6, marginTop: 16 }}><span>Comprovante de pagamento ({p.analiseManual ? "obrigatório" : "opcional"}, PDF ou imagem, até 5 MB)</span>
      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" aria-label="Comprovante do pagamento" onChange={(e) => {
        const file = e.target.files?.[0]; setArquivo(file); setErroArquivo(file && file.size > 5 * 1024 * 1024 ? "O arquivo deve ter até 5 MB." : null);
      }} />
    </label>
    {erroArquivo ? <p role="alert">{erroArquivo}</p> : null}
    {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível informar o pagamento.")}</p> : null}
  </Modal>;
}

export function DecisaoCredoraModal({ p, decisao, onClose, onFeito }: { p: Portabilidade; decisao: "aceitar" | "recusar"; onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const [motivo, setMotivo] = useState("");
  const [saldo, setSaldo] = useState<number | null>(null);
  const mut = useMutation({ mutationFn: () => atlas.portabilidade.decidirComoCredora(p.id, { decisao, motivo: motivo.trim() || undefined, saldoDevedor: decisao === "aceitar" ? saldo! : undefined }), onSuccess: onFeito });
  return <Modal open onClose={onClose} title={decisao === "aceitar" ? "Aceitar portabilidade" : "Recusar portabilidade"}
    footer={<><Button variant="ghost" onClick={onClose}>Voltar</Button><Button onClick={() => mut.mutate()} disabled={mut.isPending || (decisao === "recusar" ? !motivo.trim() : !(saldo && saldo > 0))}>{mut.isPending ? "Salvando…" : decisao === "aceitar" ? "Aceitar e informar saldo" : "Confirmar decisão"}</Button></>}>
    <ResumoPortabilidade p={p} />
    <p>{decisao === "aceitar" ? "Ao aceitar, informe o saldo de quitação. A portabilidade seguirá para pagamento pela consignatária de destino." : "A recusa encerra a portabilidade e mantém o contrato na credora atual."}</p>
    {decisao === "aceitar" ? <CurrencyField label="Saldo para quitação" required value={saldo} onValueChange={setSaldo} hint={`Saldo registrado: ${fmtBRL(p.origem.saldoDevedor)}. Informe o saldo de quitação atualizado.`} /> : null}
    {decisao === "recusar" ? <TextareaField label="Motivo da recusa" required value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={2000} /> : null}
    {mut.error ? <p role="alert">{mensagemDeErro(mut.error, "Não foi possível registrar a decisão.")}</p> : null}
  </Modal>;
}

export function AnalisarComprovanteModal({ p, onClose, onFeito }: { p: Portabilidade; onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const [motivo, setMotivo] = useState("");
  const [erroDownload, setErroDownload] = useState<string | null>(null);
  const mut = useMutation({ mutationFn: (decisao: "aprovar" | "reprovar") => atlas.portabilidade.analisarComprovante(p.id, p.versao, decisao, motivo.trim() || undefined), onSuccess: onFeito });
  return <Modal open onClose={onClose} title="Analisar comprovante de pagamento" size="lg"
    footer={<><Button variant="ghost" onClick={onClose}>Fechar</Button><Button variant="ghost" disabled={mut.isPending || !motivo.trim()} onClick={() => mut.mutate("reprovar")}>Solicitar correção</Button><Button disabled={mut.isPending} onClick={() => mut.mutate("aprovar")}>{mut.isPending ? "Processando…" : "Aprovar e liquidar"}</Button></>}>
    <ResumoPortabilidade p={p} />
    <p>Valor pago: {fmtBRL(p.valorPago ?? 0)}. Ao aprovar, a origem será quitada e o contrato de destino será averbado.</p>
    <Button variant="ghost" onClick={() => { setErroDownload(null); void downloadAuthed(atlas.portabilidade.comprovanteAdminUrl(p.id), p.comprovanteNome ?? "comprovante").catch((e) => setErroDownload(mensagemDeErro(e, "Não foi possível baixar o comprovante."))); }}>Baixar {p.comprovanteNome ?? "comprovante"}</Button>
    <TextareaField label="Motivo (obrigatório para solicitar correção)" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={2000} />
    {erroDownload ? <p role="alert">{erroDownload}</p> : null}
    {mut.error ? <p role="alert">{mensagemDeErro(mut.error, "Não foi possível analisar o comprovante.")}</p> : null}
  </Modal>;
}

/** Credora: liquidar — quita a origem e averba o destino, numa transacao so'. */
export function LiquidarPortabilidadeModal({ p, onClose, onFeito }: { p: Portabilidade; onClose: () => void; onFeito: (p: Portabilidade) => void }) {
  const mut = useMutation({ mutationFn: () => atlas.portabilidade.liquidar(p.id), onSuccess: onFeito });
  const c = p.condicoes;
  return (
    <Modal
      open
      onClose={onClose}
      danger
      eyebrow={`Portabilidade ${p.id}`}
      title="Liquidar contrato"
      subtitle={`O contrato ${p.origem.adf} vira Quitado (sai da folha a partir da próxima competência) e o contrato de ${p.proponente.nome} nasce averbado. Não há como desfazer.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Liquidando…" : "Confirmar liquidação"}
          </Button>
        </>
      }
    >
      <ResumoPortabilidade p={p} />
      {c ? (
        <p style={{ fontSize: 13, margin: 0 }}>
          {p.valorPago != null ? `Pagamento à credora: ${fmtBRL(p.valorPago)}` : "Pagamento informado anteriormente; valor pago não registrado separadamente"} · {c.prazo}x de {fmtBRL(c.valorParcela)} · contrato {c.numeroContratoBanco ?? "—"}.
        </p>
      ) : null}
      {p.comprovanteNome ? <Button variant="ghost" onClick={() => void downloadAuthed(atlas.portabilidade.comprovanteUrl(p.id), p.comprovanteNome!)}>Baixar comprovante</Button> : null}
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível liquidar.")}</p> : null}
    </Modal>
  );
}

export function CancelarPortabilidadeModal({
  p,
  quem,
  onClose,
  onFeito,
}: {
  p: Portabilidade;
  quem: "banco" | "servidor";
  onClose: () => void;
  onFeito: (p: Portabilidade) => void;
}) {
  const [motivo, setMotivo] = useState("");
  const mut = useMutation({
    mutationFn: () =>
      quem === "banco"
        ? atlas.portabilidade.cancelarComoBanco(p.id, motivo.trim() || undefined)
        : atlas.portabilidade.cancelarComoServidor(p.id, motivo.trim() || undefined),
    onSuccess: onFeito,
  });
  const recusandoPedido = quem === "banco" && p.solicitadoPor === "servidor" && p.estado === "aguardando_proposta";
  return (
    <Modal
      open
      onClose={onClose}
      danger
      eyebrow={`Portabilidade ${p.id}`}
      title={recusandoPedido ? "Recusar pedido de portabilidade" : "Cancelar portabilidade"}
      subtitle={`O contrato ${p.origem.adf} continua com ${p.credora.nome}.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Voltar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
            {mut.isPending ? "Salvando…" : recusandoPedido ? "Recusar pedido" : "Cancelar portabilidade"}
          </Button>
        </>
      }
    >
      <TextareaField label="Motivo (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível cancelar.")}</p> : null}
    </Modal>
  );
}

/** Detalhe com a trilha da esteira — o mesmo para banco e averbadora. */
export function PortabilidadeDetalheModal({
  id,
  carregar,
  onClose,
}: {
  id: string;
  carregar: (id: string) => Promise<PortabilidadeDetalhe>;
  onClose: () => void;
}) {
  const q = useQuery({ queryKey: ["portabilidade", "detalhe", id], queryFn: () => carregar(id) });
  const p = q.data;
  return (
    <Modal open onClose={onClose} size="lg" eyebrow="Portabilidade" title={id} showClose>
      {q.isLoading ? <p>Carregando…</p> : null}
      {q.error ? <p style={{ color: "var(--danger-500)" }}>{mensagemDeErro(q.error, "Não foi possível carregar a portabilidade.")}</p> : null}
      {p ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <Pill variant={pillDaPortabilidade(p.estado)}>{ESTADO_PORTABILIDADE_OPERADOR[p.estado]}</Pill>
          </div>
          <ResumoPortabilidade p={p} />
          {p.condicoes ? (
            <p style={{ fontSize: 13 }}>
              Destino: {fmtBRL(p.condicoes.valorFinanciado)} · {p.condicoes.prazo}x de {fmtBRL(p.condicoes.valorParcela)} · CET{" "}
              {(p.condicoes.cetAm * 100).toFixed(2).replace(".", ",")}% a.m.{p.adfDestino ? ` · contrato ${p.adfDestino}` : ""}
            </p>
          ) : null}
          {p.motivo ? <p style={{ fontSize: 13, color: "var(--text-muted)" }}>Motivo: {p.motivo}</p> : null}
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, display: "grid", gap: 4 }}>
            {p.eventos.map((e, i) => (
              <li key={i}>
                <b>{fmtDataHora(e.ocorridoEm)}</b> — {e.evento.replaceAll("_", " ")} → {ESTADO_PORTABILIDADE_OPERADOR[e.paraEstado as keyof typeof ESTADO_PORTABILIDADE_OPERADOR] ?? e.paraEstado}
                <span style={{ color: "var(--text-dim)" }}> · {e.ator}</span>
              </li>
            ))}
          </ol>
        </>
      ) : null}
    </Modal>
  );
}
